import type { Cue } from '@core/model/cue.ts';
import {
  createJob,
  getJob,
  mapSoraCreateFailureToLegacy,
  mapSoraJobToLegacy,
  shouldClearCachedSoraJobId,
  uploadNetflixSubtitles,
  type SoraAuthOpts,
  type SoraClientOptions,
} from '../net/soraClient.ts';
import {
  batchTranslateProgressText,
  serviceDismissText,
  translationMemberOnlyText,
} from '../../lib/serviceStatusMessages.ts';

export type BatchTranslateOpts = SoraClientOptions &
  SoraAuthOpts & {
    videoId: string;
    /** Track id for cache key (extension: vssId; mobile: selected menu value). */
    trackId: string;
    targetLang: string;
    cues: Cue[];
    /** Poll interval ms (default 1500). */
    pollMs?: number;
    /** Called after cue.translation progress text is written (invalidate overlay). */
    onProgress?: (message: string) => void;
    signal?: AbortSignal;
  };

const batchJobIds = new Map<string, string>();

/** Extension: `${movieId}-${trackId}` then upload + translate source=netflix. */
export function batchCacheId(videoId: string, trackId: string): string {
  return `${videoId}-${trackId || '0'}`;
}

function batchKey(cacheId: string, lang: string): string {
  return `${cacheId}:${lang}`;
}

export function resetBatchJobIdsForTests(): void {
  batchJobIds.clear();
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const t = window.setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

function cuesForUpload(cues: Cue[]): unknown[] {
  return cues.map((cue) => ({
    start: cue.start,
    end: cue.end,
    text: cue.text,
    segs:
      cue.segs?.length > 0
        ? cue.segs
        : [{ start: cue.start, end: cue.end, text: cue.text }],
  }));
}

function applyBatchResult(cues: Cue[], result: unknown): void {
  const rows = Array.isArray(result) ? result : [];
  for (let i = 0; i < cues.length; i += 1) {
    const cue = cues[i];
    if (!cue) continue;
    const row = rows[i] as { segs?: Array<{ text?: string }> } | undefined;
    const text = row?.segs?.[0]?.text?.replaceAll(/<br[^>]*>/gi, '\n') ?? '';
    cue.translation = text;
    cue.translated = true;
    cue.loading = false;
  }
}

function applyDismiss(cues: Cue[], message: string): void {
  for (const cue of cues) {
    cue.translation = message;
    cue.translated = true;
    cue.loading = false;
  }
}

/** Extension: write generating/queuing onto every cue's translation line. */
function applyBatchProgress(
  cues: Cue[],
  message: string,
  onProgress?: (message: string) => void,
): void {
  for (const cue of cues) {
    cue.translated = true;
    cue.translation = message;
    cue.loading = false;
  }
  onProgress?.(message);
}

/**
 * Full-track AI Batch — mirrors extension translationLegacyBridge:
 * 1) POST /v1/netflix/:id/subtitles with current cues
 * 2) POST /v1/jobs { type:'translate', source:'netflix', videoId:id, targetLang }
 * 3) poll until done — progress text goes on cue.translation (not cue.loading).
 */
export async function runBatchTranslate(opts: BatchTranslateOpts): Promise<void> {
  const {
    videoId,
    trackId,
    targetLang,
    cues,
    pollMs = 1500,
    onProgress,
    signal,
    fetchImpl = fetch.bind(globalThis),
    baseUrl,
    apiKey,
    isMember = true,
    userId = '',
  } = opts;

  if (!cues.length) return;
  const auth: SoraAuthOpts = { isMember, userId };
  const cacheId = batchCacheId(videoId, trackId);
  const key = batchKey(cacheId, targetLang);

  applyBatchProgress(cues, batchTranslateProgressText({ position: 1 }), onProgress);

  try {
    let jobId = batchJobIds.get(key);
    if (!jobId) {
      await uploadNetflixSubtitles(
        cacheId,
        cuesForUpload(cues),
        auth,
        fetchImpl,
        baseUrl,
        apiKey,
      );

      const created = await createJob(
        { type: 'translate', source: 'netflix', videoId: cacheId, targetLang },
        auth,
        fetchImpl,
        baseUrl,
        apiKey,
      );

      if (created.httpStatus === 200 && created.status === 'done' && created.result != null) {
        applyBatchResult(cues, created.result);
        return;
      }

      const legacyFail = mapSoraCreateFailureToLegacy(created);
      if (legacyFail) {
        applyDismiss(cues, serviceDismissText(legacyFail.desc));
        return;
      }
      if (created.httpStatus === 403 || created.error === 'members_only') {
        applyDismiss(cues, translationMemberOnlyText());
        return;
      }
      if (!created.jobId) {
        applyDismiss(cues, serviceDismissText(created.error));
        return;
      }
      jobId = created.jobId;
      batchJobIds.set(key, jobId);
    }

    for (;;) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const poll = await getJob(jobId!, auth, fetchImpl, baseUrl, apiKey);
      const legacy = poll ? mapSoraJobToLegacy(poll) : undefined;

      if (legacy?.response === 'OK' && legacy.result != null) {
        applyBatchResult(cues, legacy.result);
        batchJobIds.delete(key);
        return;
      }
      if (legacy?.response === 'Dismiss') {
        applyDismiss(
          cues,
          legacy.desc === 'members_only'
            ? translationMemberOnlyText()
            : serviceDismissText(legacy.desc),
        );
        batchJobIds.delete(key);
        return;
      }
      if (legacy?.response === 'Failed' || poll?.status === 'failed' || poll?.status === 'cancelled') {
        applyDismiss(cues, serviceDismissText(legacy?.desc ?? poll?.error));
        batchJobIds.delete(key);
        return;
      }

      if (shouldClearCachedSoraJobId(poll) && poll?.status !== 'done') {
        batchJobIds.delete(key);
      }

      if (legacy?.response === 'Queuing' || legacy?.position != null || legacy?.progress != null) {
        applyBatchProgress(
          cues,
          batchTranslateProgressText({
            position: legacy?.position ?? 1,
            progress: legacy?.progress,
          }),
          onProgress,
        );
      }

      await sleep(pollMs, signal);
    }
  } catch (err) {
    for (const cue of cues) {
      if (cue.loading) cue.loading = false;
    }
    throw err;
  }
}
